import type { AdminSubmission } from '../lib/types.ts';

type ReplyFields = Pick<AdminSubmission, 'latest_comment_author' | 'status' | 'resubmitted_at' | 'reviewed_at'>;

/**
 * True when the ball is in the admin's court: the vendor sent the most recent
 * comment on this submission and the admin hasn't responded, AND the document
 * isn't already marked Compliant (status 'approved'). Drives the "Reply needed"
 * badge in the admin review table / submission rows.
 *
 * 'approved' is the per-document "Compliant" state (the green review button), so
 * once a document is approved we stop nudging for a reply even if the vendor
 * commented last.
 *
 * Also returns true when the vendor explicitly resubmitted the answer (resubmitted_at
 * is set) and it's not yet approved — this lights the signal even with no new comment,
 * since the resubmit is itself the vendor's action.
 */
export function awaitingAdminReply(submission: ReplyFields | undefined): boolean {
  if (!submission) return false;
  if (submission.status === 'approved') return false;
  // An explicit resubmit lights the signal until the admin responds. Any
  // reviewed_at newer than the resubmit (a verdict OR a standalone comment)
  // resolves it; verdicts also clear resubmitted_at outright.
  if (
    submission.resubmitted_at != null &&
    (submission.reviewed_at == null || submission.reviewed_at < submission.resubmitted_at)
  ) {
    return true;
  }
  return submission.latest_comment_author === 'vendor';
}

/** How many of a vendor's submissions are awaiting an admin reply. */
export function awaitingReplyCount(submissions: ReplyFields[]): number {
  return submissions.reduce((n, s) => (awaitingAdminReply(s) ? n + 1 : n), 0);
}
